import React, { useContext, useEffect } from 'react';
import {
	IEditModifierRestaurantPropertiesContext,
	EditModifierRestaurantPropertiesContext,
} from '../../../../editModifierRestaurantPropertiesState/editModifierRestaurantPropertiesContext';
import { Form } from 'antd';
import { preventDefaultForEnterKey } from 'utils/helpers';
import Text from 'components/Text/Text';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { RestaurantSelect } from './components/RestaurantSelect';
import { PriceInput } from './components/PriceInput';
import { SupplementPriceInput } from './components/SupplementPriceInput';
import { QuantityMaxLimitInput } from './components/QuantityMaxLimitInput';
import { PortionSelectionModeRadio } from './components/PortionSelectionModeRadio';
import { InStopCheckbox } from './components/InStopCheckbox';
import { IsRestrictedCheckbox } from './components/IsRestrictedCheckbox';
import { IsPreselectedCheckbox } from './components/IsPreselectedCheckbox';
import { ForceDisableCheckbox } from './components/ForceDisableCheckbox';
import { ActivationScheduleInput } from './components/ActivationScheduleInput';
import { DeactivationScheduleInput } from './components/DeactivationScheduleInput';
import CronGenerator from 'components/CronGenerator/CronGenerator';
import { ModifierGroupsMultipleSelect } from './components/ModifierGroupsMultipleSelect';
import { BtnBlock } from './components/BtnBlock';
import style from './InputFields.module.scss';
import { Link } from 'react-router-dom';
import { Badge } from 'components/Badge';

export const InputFields: React.FC = () => {
	const {
		setIDRestaurant,
		changePrice,
		changeSupplementPrice,
		changeQuantityMaxLimit,
		toggleInStop,
		toggleIsRestricted,
		toggleIsPreselected,
		toggleForceDisable,
		changePortionSelectionMode,
		changeActivationSchedule,
		changeDeactivationSchedule,
		setModifierGroups,
		setModifierGroupsWithAdditionalData,
		setDataForSubmit,
		changeTypeOfModalWindow,
		modifierId,
		modifierName,
		customerName,
		fetchedRestaurants,
		IDSelectedRestaurant,
		price,
		supplementPrice,
		isCreatedFromPos,
		quantityMaxLimit,
		inStop,
		isRestricted,
		isPreselected,
		forceDisable,
		portionSelectionMode,
		activationSchedule,
		deactivationSchedule,
		parentModifierGroups,
		IDsSelectedModifierGroups,
		modifierGroupsWithAdditionalData,
		hasPortions,
		hasPortionBasedNestedModGroups,
	} = useContext<IEditModifierRestaurantPropertiesContext>(EditModifierRestaurantPropertiesContext);
	const [form] = Form.useForm();

	useEffect(() => {
		form.setFieldsValue({
			IDSelectedRestaurant,
			price,
			supplementPrice,
			quantityMaxLimit,
			inStop,
			isRestricted,
			isPreselected,
			forceDisable,
			portionSelectionMode,
			activationSchedule,
			deactivationSchedule,
			parentModifierGroups,
			IDsSelectedModifierGroups,
		});
		modifierGroupsWithAdditionalData.forEach((el) => {
			form.setFieldsValue({
				[`downLimit-${el.id}`]: el.downLimit,
				[`upLimit-${el.id}`]: el.upLimit,
			});
		});
	}, [
		IDSelectedRestaurant,
		price,
		supplementPrice,
		quantityMaxLimit,
		inStop,
		isRestricted,
		isPreselected,
		forceDisable,
		activationSchedule,
		deactivationSchedule,
		parentModifierGroups,
		IDsSelectedModifierGroups,
		modifierGroupsWithAdditionalData,
		hasPortions,
		portionSelectionMode,
	]);

	const submit = async () => {
		const modifierGroups = modifierGroupsWithAdditionalData.map((modifierGroupWithAdditionalData) => ({
			id: modifierGroupWithAdditionalData.id,
			upLimit: modifierGroupWithAdditionalData.upLimit,
			downLimit: modifierGroupWithAdditionalData.downLimit,
		}));

		setDataForSubmit({
			restaurantId: IDSelectedRestaurant || '',
			modifierId,
			price,
			supplementPrice,
			quantityMaxLimit,
			isDisabled: inStop,
			isRestricted: isRestricted,
			isPreselected: isPreselected,
			forceDisable: forceDisable,
			activationCron: activationSchedule,
			deactivationCron: deactivationSchedule,
			portionSelectionMode,
			modifierGroups,
		});
		changeTypeOfModalWindow('toConfirmSubmit');
	};

	const revalidateForm = form.validateFields;

	return (
		<div data-test="InputFields">
			<Form form={form} name="basic" onFinish={submit} onKeyPress={preventDefaultForEnterKey} layout="vertical">
				<Text
					className={style.nameOfModifier}
					translationNameSpace="form"
					translationKey={modifierName}
					size="xxl"
					weight="bold"
					display="block"
				/>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="restaurantPropertiesHeading"
						theme="secondary"
						size="l"
						weight="bold"
						display="inline"
					/>
					<div className={style.mainProps}>
						<FullInfoField label="customerLabel" value={customerName} useInForm={true} />
						<div />
					</div>
					<RestaurantSelect
						setIDRestaurant={setIDRestaurant}
						fetchedRestaurants={fetchedRestaurants}
						selectedRestaurantId={IDSelectedRestaurant}
					/>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="scheduleHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div className={style.row}>
						<ActivationScheduleInput changeActivationSchedule={changeActivationSchedule} />
						<DeactivationScheduleInput changeDeactivationSchedule={changeDeactivationSchedule} />
					</div>
					<CronGenerator />
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="priceHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<PortionSelectionModeRadio
						changePortionSelectionMode={changePortionSelectionMode}
						isDisabled={!IDSelectedRestaurant || isCreatedFromPos}
					/>
					<div>
						<div>
							<div className={hasPortions ? style.column : style.row}>
								{hasPortions ? (
									<Text
										translationNameSpace="form"
										translationKey="warningPriceSupplementPriceHiddenByModifierPortions"
										theme="secondary"
										size="s"
										display="inline"
									/>
								) : (
									<>
										<PriceInput changePrice={changePrice} disabled={isCreatedFromPos} />
										<SupplementPriceInput
											changeSupplementPrice={changeSupplementPrice}
											disabled={isCreatedFromPos}
										/>
									</>
								)}
								<QuantityMaxLimitInput changeQuantityMaxLimit={changeQuantityMaxLimit} />
							</div>
							<div>
								<InStopCheckbox
									toggleInStop={toggleInStop}
									inStop={inStop}
									isDisabled={!IDSelectedRestaurant}
								/>
								<IsRestrictedCheckbox
									toggleIsRestricted={toggleIsRestricted}
									isRestricted={isRestricted}
									isDisabled={!IDSelectedRestaurant}
								/>
								<IsPreselectedCheckbox
									toggleIsPreselected={toggleIsPreselected}
									isPreselected={isPreselected}
									isDisabled={!IDSelectedRestaurant}
								/>
								<ForceDisableCheckbox
									toggleForceDisable={toggleForceDisable}
									forceDisable={forceDisable}
									isDisabled={!IDSelectedRestaurant}
								/>
							</div>
						</div>
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="modifierParentModifierGroupsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					<div>
						{parentModifierGroups.length ? (
							<div className={style.elementsList}>
								{parentModifierGroups.map((modifierGroup) => {
									return (
										<div key={modifierGroup.id} className={style.elementItem}>
											<div className={style.dot} />
											<Link
												className={style.link}
												target={`blank_${Date.now()}`}
												rel="noopener noreferrer"
												to={`/menu-v2/modifier-groups/view?modifierGroupID=${modifierGroup.id}`}>
												<Text
													className={style.name}
													translationKey={modifierGroup.name}
													size="s"
													withTooltip={true}
												/>
											</Link>
											{modifierGroup.isDefault && (
												<Badge color="green" size="sm">
													<Text
														translationNameSpace="form"
														translationKey="isDefaultLabel"
														size="xs"
													/>
												</Badge>
											)}
											{modifierGroup.isAssignedToPortion && (
												<Badge color="blue" size="sm">
													<Text
														translationNameSpace="form"
														translationKey="posPortionLabel"
														size="xs"
													/>
												</Badge>
											)}
										</div>
									);
								})}
							</div>
						) : (
							<Text translationNameSpace="form" translationKey="-" />
						)}
					</div>
				</div>
				<div className={style.fieldsWrapper}>
					<Text
						translationNameSpace="form"
						translationKey="modifierChildModifierGroupsHeading"
						theme="secondary"
						size="l"
						weight="bold"
					/>
					{hasPortionBasedNestedModGroups ? (
						<Text
							translationNameSpace="form"
							translationKey="warningNestedModGroupsHiddenByModifierPortions"
							theme="secondary"
							size="s"
							display="inline"
						/>
					) : (
						<ModifierGroupsMultipleSelect
							setModifierGroups={setModifierGroups}
							setModifierGroupsWithAdditionalData={setModifierGroupsWithAdditionalData}
							productId={modifierId}
							restaurantId={IDSelectedRestaurant}
							IDsSelectedModifierGroups={IDsSelectedModifierGroups}
							modifierGroupsWithAdditionalData={modifierGroupsWithAdditionalData}
						/>
					)}
				</div>
				<BtnBlock revalidateForm={revalidateForm} />
			</Form>
		</div>
	);
};
